// Accueil du personnel (M11) : synthèse agrégée (nombres seulement, sans nom ni motif) pour tout détenteur
// du domaine personnel ; liste des agents et contrats à échéance réservés aux RH habilités (rôle et
// habilitation nominative personnel_sensible), avec un bandeau honnête pour les autres.
import { LIBELLES_ETAT_CONGE, LIBELLES_ETAT_PRESENCE, LIBELLES_STATUT_AGENT, STATUTS_AGENT, type EtatCongePersonnel } from '@cc/contrats';
import { Bandeau, Bouton, Champ, Dialogue, EtatVide, Lien, LienBouton, Panneau, Selection, Tableau, type ColonneTableau } from '@cc/ui';
import { CalendarCheck, ClipboardList, GraduationCap, Plus, UserRound, Users } from 'lucide-react';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { creerAgent, INVALIDER_PERSONNEL, useAgents, useEcheances, useSynthese, type AgentPersonnel, type EcheanceContrat } from './api';
import { aujourdhuiIso } from './logique';
import commun from '../../commun/commun.module.css';
import styles from '../proprete/proprete.module.css';

function Synthese() {
  const s = useSynthese();
  const d = s.data;
  return (
    <Panneau titre="Synthèse du personnel" niveau={2}>
      <EtatRequete chargement={s.isPending} erreur={s.error} onReessayer={() => void s.refetch()} lot="L5-A" fonction="La synthèse du personnel">
        {d ? (
          <dl className={commun.definitions}>
            <dt>Date de référence</dt><dd>{date(d.date_reference)}</dd>
            <dt>Agents en fonction</dt><dd>{d.agents}</dd>
            <dt>Par statut</dt><dd>{d.par_statut.length ? d.par_statut.map((x) => `${LIBELLES_STATUT_AGENT[x.statut as keyof typeof LIBELLES_STATUT_AGENT] ?? x.statut} : ${x.nombre}`).join(' ; ') : 'aucun agent'}</dd>
            <dt>Par service</dt><dd>{d.par_service.length ? d.par_service.map((x) => `${x.service} : ${x.nombre}`).join(' ; ') : 'aucune affectation en cours'}</dd>
            <dt>Présences du jour</dt><dd>{d.presences_du_jour.length ? d.presences_du_jour.map((x) => `${LIBELLES_ETAT_PRESENCE[x.etat as keyof typeof LIBELLES_ETAT_PRESENCE] ?? x.etat} : ${x.nombre}`).join(' ; ') : 'non saisies'}</dd>
            <dt>Congés en cours</dt><dd>{d.conges_en_cours}</dd>
            <dt>Demandes en attente</dt><dd>{d.demandes_en_attente.length ? d.demandes_en_attente.map((x) => `${LIBELLES_ETAT_CONGE[x.etat as EtatCongePersonnel] ?? x.etat} : ${x.nombre}`).join(' ; ') : 'aucune'}</dd>
            <dt>Contrats à échéance sous 60 jours</dt><dd>{d.contrats_echeance_60_jours}</dd>
            <dt>Sessions de formation à venir</dt><dd>{d.formations_a_venir}</dd>
          </dl>
        ) : null}
      </EtatRequete>
    </Panneau>
  );
}

function DialogueAgent({ onFermer }: { onFermer: () => void }) {
  const naviguer = useNavigate();
  const [v, setV] = useState({ matricule: '', nom: '', prenoms: '', statut: 'fonctionnaire', date_entree: aujourdhuiIso(), grade: '', echelon: '' });
  const maj = (k: keyof typeof v) => (e: { target: { value: string } }) => setV((x) => ({ ...x, [k]: e.target.value }));
  const action = useAction({
    executer: () => creerAgent({ matricule: v.matricule, nom: v.nom, prenoms: v.prenoms, statut: v.statut, date_entree: v.date_entree, grade: v.grade || undefined, echelon: v.echelon || undefined }),
    succes: (a) => `Agent ${a.matricule} enregistré.`,
    invalider: INVALIDER_PERSONNEL,
    apres: (a) => void naviguer(`/personnel/agents/${a.id}`),
  });
  const complet = v.matricule.trim() && v.nom.trim() && v.prenoms.trim() && v.date_entree;
  return (
    <Dialogue ouvert titre="Enregistrer un agent" description="La fiche d'agent est distincte du compte de connexion ; aucune donnée de paie n'est saisie." onFermer={onFermer}
      actions={<><Bouton variante="secondaire" onClick={onFermer}>Annuler</Bouton><Bouton variante="principal" chargement={action.enCours} disabled={!complet} onClick={() => action.lancer(undefined)}>Enregistrer</Bouton></>}>
      <div className={styles.grilleFormulaire}>
        <Champ libelle="Matricule" value={v.matricule} onChange={maj('matricule')} required />
        <Champ libelle="Nom" value={v.nom} onChange={maj('nom')} required />
        <Champ libelle="Prénoms" value={v.prenoms} onChange={maj('prenoms')} required />
        <Selection libelle="Statut" options={STATUTS_AGENT.map((s) => ({ valeur: s, libelle: LIBELLES_STATUT_AGENT[s] }))} value={v.statut} onChange={maj('statut')} />
        <Champ libelle="Date d'entrée" type="date" value={v.date_entree} onChange={maj('date_entree')} required />
        <Champ libelle="Grade" value={v.grade} onChange={maj('grade')} facultatif />
        <Champ libelle="Échelon" value={v.echelon} onChange={maj('echelon')} facultatif />
      </div>
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

function Agents() {
  const { contexte } = useSession();
  const [recherche, setRecherche] = useState('');
  const [creation, setCreation] = useState(false);
  const agents = useAgents(recherche);
  const echeances = useEcheances();
  const colonnes: ColonneTableau<AgentPersonnel>[] = [
    { id: 'matricule', entete: 'Matricule', enTeteLigne: true, masquable: false, cellule: (a) => <Lien href={`/personnel/agents/${a.id}`} className={commun.reference}>{a.matricule}</Lien>, valeurTri: (a) => a.matricule },
    { id: 'nom', entete: 'Nom et prénoms', cellule: (a) => `${a.nom} ${a.prenoms}`, valeurTri: (a) => `${a.nom} ${a.prenoms}` },
    { id: 'statut', entete: 'Statut', cellule: (a) => LIBELLES_STATUT_AGENT[a.statut] },
    { id: 'grade', entete: 'Grade et échelon', cellule: (a) => [a.grade, a.echelon].filter(Boolean).join(', ') || 'non renseigné' },
    { id: 'entree', entete: 'Entrée', cellule: (a) => date(a.date_entree), valeurTri: (a) => a.date_entree },
  ];
  const colonnesEcheances: ColonneTableau<EcheanceContrat>[] = [
    { id: 'reference', entete: 'Contrat', enTeteLigne: true, masquable: false, cellule: (c) => c.reference },
    { id: 'agent', entete: 'Agent', cellule: (c) => <Lien href={`/personnel/agents/${c.agent_id}`}>{`${c.nom} ${c.prenoms} (${c.matricule})`}</Lien> },
    { id: 'nature', entete: 'Nature', cellule: (c) => c.nature },
    { id: 'fin', entete: 'Échéance', cellule: (c) => `${date(c.fin)} (dans ${c.jours_restants} j)`, valeurTri: (c) => c.fin },
  ];
  return (
    <>
      <Panneau titre="Contrats arrivant à échéance sous 60 jours" niveau={2}>
        <EtatRequete chargement={echeances.isPending} erreur={echeances.error} onReessayer={() => void echeances.refetch()} lot="L5-A" fonction="Les échéances des contrats">
          {(echeances.data?.elements ?? []).length === 0 ? <p>Aucun contrat non renouvelé n&apos;arrive à échéance dans les 60 jours.</p> : <Tableau legende="Contrats à échéance" colonnes={colonnesEcheances} lignes={echeances.data?.elements ?? []} cleLigne={(c) => c.id} cleColonnes="personnel-echeances" />}
        </EtatRequete>
      </Panneau>
      <Panneau titre="Agents" niveau={2} actions={peut(contexte, 'personnel_sensible', 'creer') ? <Bouton variante="principal" icone={Plus} onClick={() => setCreation(true)}>Enregistrer un agent</Bouton> : null}>
        <Champ libelle="Rechercher (matricule, nom, prénoms)" type="search" value={recherche} onChange={(e) => setRecherche(e.target.value)} facultatif />
        <EtatRequete chargement={agents.isPending} erreur={agents.error} onReessayer={() => void agents.refetch()} lot="L5-A" fonction="La liste des agents">
          {(agents.data?.elements ?? []).length === 0 ? <EtatVide titre="Aucun agent" icone={UserRound}><p>{recherche ? 'Aucun agent ne correspond à cette recherche.' : 'Aucun agent n\'est encore enregistré.'}</p></EtatVide> : <Tableau legende="Agents" colonnes={colonnes} lignes={agents.data?.elements ?? []} cleLigne={(a) => a.id} cleColonnes="personnel-agents" />}
        </EtatRequete>
      </Panneau>
      {creation ? <DialogueAgent onFermer={() => setCreation(false)} /> : null}
    </>
  );
}

export function PagePersonnel() {
  const { contexte } = useSession();
  const habilite = peut(contexte, 'personnel_sensible', 'voir');
  // Sans le domaine personnel, l'entrée du menu mène à l'espace personnel de l'agent.
  if (contexte && !peut(contexte, 'personnel', 'voir')) return <Navigate to="/personnel/moi" replace />;
  return (
    <>
      <EnTetePage etiquette="Ressources humaines" titre="Personnel" description="Agents, affectations, contrats, présences, congés, formations et évaluations, sans paie. Les exports CSV alimentent un traitement de paie externe."
        actions={<>
          <LienBouton href="/personnel/conges" icone={CalendarCheck} variante="secondaire">Congés</LienBouton>
          <LienBouton href="/personnel/presences" icone={ClipboardList} variante="secondaire">Présences</LienBouton>
          {habilite ? <LienBouton href="/personnel/formations" icone={GraduationCap} variante="secondaire">Formations</LienBouton> : null}
          {habilite ? <LienBouton href="/personnel/evaluations" icone={Users} variante="secondaire">Évaluations</LienBouton> : null}
          <LienBouton href="/personnel/moi" icone={UserRound} variante="secondaire">Mon espace</LienBouton>
        </>} />
      <div className={commun.pile}>
        <Synthese />
        {habilite ? <Agents /> : (
          <Bandeau type="info">
            Les fiches individuelles, les contrats et les échéances nominatives exigent le rôle des ressources humaines et une habilitation nominative « personnel sensible » validée et en vigueur. Seule la synthèse agrégée, sans nom ni motif, est affichée ici.
          </Bandeau>
        )}
      </div>
    </>
  );
}
